import { record } from 'rrweb';
import { getAssignmentClientSessionId } from 'utils/assignmentSessionTracking';
import { REPLAY_FORMAT_RRWEB } from 'utils/assignmentSessionReplayLoad';

export const ASSIGNMENT_REPLAY_VERSION = 2;
export const ASSIGNMENT_REPLAY_ROOT_SELECTOR = '[data-owe-assignment-session]';
export const ASSIGNMENT_REPLAY_CHUNK_EVENT_LIMIT = 350;
export const ASSIGNMENT_REPLAY_UPLOAD_INTERVAL_MS = 60000;
export const ASSIGNMENT_REPLAY_SNAPSHOT_INTERVAL_MS = 90000;
export const ASSIGNMENT_REPLAY_SNAPSHOT_HTML_MAX = 180000;
export const ASSIGNMENT_REPLAY_REGISTRY_CAP = 24;
export const ASSIGNMENT_REPLAY_CHUNKS_PER_SESSION_CAP = 40;

export function getElementPath(el, maxDepth = 10) {
    if (!el || el.nodeType !== 1) return '';
    const parts = [];
    let node = el;
    while (node && node.nodeType === 1 && parts.length < maxDepth) {
        let part = node.tagName ? node.tagName.toLowerCase() : 'node';
        if (node.id) {
            part += `#${String(node.id).slice(0, 80)}`;
            parts.unshift(part);
            break;
        }
        const parent = node.parentElement;
        if (parent) {
            const siblings = Array.from(parent.children).filter((c) => c.tagName === node.tagName);
            if (siblings.length > 1) {
                const idx = siblings.indexOf(node) + 1;
                part += `:nth-of-type(${idx})`;
            }
        }
        parts.unshift(part);
        node = parent;
    }
    return parts.join(' > ');
}

export function sanitizeSnapshotHtml(html) {
    if (!html || typeof html !== 'string') return '';
    let out = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    out = out.replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '');
    out = out.replace(/<input\b([^>]*)\btype\s*=\s*["']?password["']?([^>]*)>/gi, '<input$1type="password"$2 value="">');
    return out;
}

export function replayEntryMatchesSubmissionScope(entry, submissionId, logId) {
    if (!entry || typeof entry !== 'object') return false;
    const entrySub = entry.submission_id ?? entry.submissionId;
    if (submissionId != null && entrySub != null && String(entrySub) !== String(submissionId)) {
        return false;
    }
    const entryLog = entry.log_id ?? entry.logId;
    if (logId != null) {
        if (entryLog == null || String(entryLog) !== String(logId)) {
            return false;
        }
    } else if (entryLog != null) {
        return false;
    }
    return true;
}

export function clearReplayStorageForSubmission(submissionId) {
    if (!submissionId) return;
    const prefix = `owe_replay_reg_${String(submissionId)}_`;
    try {
        sessionStorage.removeItem(`owe_replay_attempt_idx_${String(submissionId)}`);
        const keysToRemove = [];
        for (let i = 0; i < sessionStorage.length; i += 1) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith(prefix)) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach((key) => sessionStorage.removeItem(key));
    } catch {
    }
}

export function patchSessionReplayRegistryScope(registry, submissionId, logId) {
    const sub = submissionId != null ? String(submissionId) : null;
    const log = logId != null ? String(logId) : null;
    return (Array.isArray(registry) ? registry : []).map((entry) => {
        if (!entry || typeof entry !== 'object') return entry;
        return {
            ...entry,
            submission_id: entry.submission_id ?? entry.submissionId ?? sub,
            log_id: entry.log_id ?? entry.logId ?? log,
        };
    });
}

export function getReplayRegistryStorageKey(submissionId, logId) {
    const sub = String(submissionId || 'unknown');
    const log = logId != null && String(logId).trim() ? String(logId).trim() : 'pending';
    return `owe_replay_reg_${sub}_${log}`;
}

export function loadReplayRegistryFromStorage(submissionId, logId) {
    try {
        const raw = sessionStorage.getItem(getReplayRegistryStorageKey(submissionId, logId));
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

export function saveReplayRegistryToStorage(submissionId, logId, registry) {
    try {
        sessionStorage.setItem(
            getReplayRegistryStorageKey(submissionId, logId),
            JSON.stringify(Array.isArray(registry) ? registry : [])
        );
    } catch {
    }
}

export function filterSessionReplaysForScope(list, submissionId, logId) {
    return (Array.isArray(list) ? list : []).filter((entry) =>
        replayEntryMatchesSubmissionScope(entry, submissionId, logId)
    );
}

export function mergeSessionReplayRegistry(serverList, localList, scope = {}) {
    const { submissionId = null, logId = null } = scope;
    const bySession = new Map();
    const ingest = (entry) => {
        if (!replayEntryMatchesSubmissionScope(entry, submissionId, logId)) return;
        const sid = entry.session_id ?? entry.sessionId;
        if (!sid) return;
        const key = String(sid);
        const prev = bySession.get(key) || {
            session_id: key,
            submission_id: entry.submission_id ?? entry.submissionId ?? submissionId ?? null,
            log_id: entry.log_id ?? entry.logId ?? logId ?? null,
            started_at: entry.started_at ?? null,
            updated_at: null,
            chunks: [],
            manifest_url: null,
        };
        const started = entry.started_at ?? prev.started_at;
        const updated = entry.updated_at ?? entry.recorded_at ?? prev.updated_at;
        const chunkMap = new Map();
        for (const c of [...(prev.chunks || []), ...(Array.isArray(entry.chunks) ? entry.chunks : [])]) {
            if (!c || typeof c !== 'object') continue;
            const idx = Number(c.index ?? c.chunk_index);
            if (!Number.isFinite(idx)) continue;
            chunkMap.set(idx, {
                index: idx,
                url: c.url ?? c.firebase_url ?? null,
                event_count: Number(c.event_count) || 0,
                recorded_at: c.recorded_at ?? null,
                byte_size: Number(c.byte_size) || 0,
            });
        }
        const chunks = Array.from(chunkMap.values())
            .filter((c) => c.url)
            .sort((a, b) => a.index - b.index)
            .slice(-ASSIGNMENT_REPLAY_CHUNKS_PER_SESSION_CAP);
        bySession.set(key, {
            session_id: key,
            submission_id: prev.submission_id ?? submissionId ?? null,
            log_id: prev.log_id ?? logId ?? null,
            started_at: started || prev.started_at,
            updated_at: updated || prev.updated_at,
            manifest_url: entry.manifest_url ?? prev.manifest_url ?? null,
            chunks,
        });
    };
    for (const e of filterSessionReplaysForScope(serverList, submissionId, logId)) ingest(e);
    for (const e of filterSessionReplaysForScope(localList, submissionId, logId)) ingest(e);
    return Array.from(bySession.values())
        .sort((a, b) => String(b.updated_at || '').localeCompare(String(a.updated_at || '')))
        .slice(0, ASSIGNMENT_REPLAY_REGISTRY_CAP);
}

export function registerReplayChunk(registry, sessionId, chunkMeta) {
    const sid = String(sessionId || '').trim();
    if (!sid || !chunkMeta?.url) return registry;
    const list = Array.isArray(registry) ? [...registry] : [];
    const idx = Number(chunkMeta.index);
    const now = chunkMeta.recorded_at || new Date().toISOString();
    const existingIdx = list.findIndex((r) => String(r.session_id) === sid);
    const base =
        existingIdx >= 0
            ? { ...list[existingIdx] }
            : {
                  session_id: sid,
                  submission_id: chunkMeta.submission_id ?? null,
                  log_id: chunkMeta.log_id ?? null,
                  started_at: chunkMeta.started_at || now,
                  updated_at: null,
                  chunks: [],
                  manifest_url: null,
              };
    const chunkMap = new Map();
    for (const c of Array.isArray(base.chunks) ? base.chunks : []) {
        if (c && Number.isFinite(Number(c.index))) chunkMap.set(Number(c.index), c);
    }
    chunkMap.set(idx, {
        index: idx,
        url: chunkMeta.url,
        event_count: Number(chunkMeta.event_count) || 0,
        recorded_at: now,
        byte_size: Number(chunkMeta.byte_size) || 0,
    });
    base.chunks = Array.from(chunkMap.values())
        .sort((a, b) => a.index - b.index)
        .slice(-ASSIGNMENT_REPLAY_CHUNKS_PER_SESSION_CAP);
    base.updated_at = now;
    if (!base.started_at) base.started_at = chunkMeta.started_at || now;
    if (existingIdx >= 0) list[existingIdx] = base;
    else list.push(base);
    return list
        .sort((a, b) => String(b.updated_at || '').localeCompare(String(a.updated_at || '')))
        .slice(0, ASSIGNMENT_REPLAY_REGISTRY_CAP);
}

export function createAssignmentSessionReplayRecorder({
    sessionId,
    assignmentId,
    submissionId,
    logId,
    onChunkReady,
}) {
    const startedAt = new Date().toISOString();
    let chunkIndex = 0;
    const rrwebEvents = [];
    let stopped = false;
    let stopRecord = null;

    const onEmit = (event) => {
        if (stopped || !event) return;
        rrwebEvents.push(event);
        if (
            rrwebEvents.length >= ASSIGNMENT_REPLAY_CHUNK_EVENT_LIMIT &&
            typeof onChunkReady === 'function'
        ) {
            onChunkReady();
        }
    };

    const takeSnapshot = () => {
        try {
            record.takeFullSnapshot?.(true);
        } catch {
        }
    };

    const drainChunk = () => {
        if (!rrwebEvents.length) return null;
        const drained = rrwebEvents.splice(0, rrwebEvents.length);
        const payload = {
            v: ASSIGNMENT_REPLAY_VERSION,
            format: REPLAY_FORMAT_RRWEB,
            session_id: sessionId,
            assignment_id: assignmentId || null,
            submission_id: submissionId || null,
            log_id: logId || null,
            chunk_index: chunkIndex,
            started_at: startedAt,
            recorded_at: new Date().toISOString(),
            viewport: {
                w: typeof window !== 'undefined' ? window.innerWidth : 0,
                h: typeof window !== 'undefined' ? window.innerHeight : 0,
            },
            page_url: typeof window !== 'undefined' ? String(window.location.href || '') : '',
            events: drained,
        };
        chunkIndex += 1;
        return payload;
    };

    const start = () => {
        stopRecord = record({
            emit: onEmit,
            maskAllInputs: true,
            sampling: {
                mousemove: 80,
                scroll: 120,
                input: 'last',
            },
            checkoutEveryNms: 3 * 60 * 1000,
        });
    };

    const stop = () => {
        if (stopped) return;
        stopped = true;
        if (typeof stopRecord === 'function') {
            try {
                stopRecord();
            } catch {
            }
            stopRecord = null;
        }
    };

    return {
        start,
        stop,
        takeSnapshot,
        drainChunk,
        get sessionId() {
            return sessionId;
        },
        get startedAt() {
            return startedAt;
        },
    };
}

export function getAssignmentReplaySessionId() {
    return getAssignmentClientSessionId();
}
